import { useState, useRef, useEffect } from 'react';
import webLogo from '../../assets/web_logo.svg';

const VOICE_CATALOG = [
    { name: 'Josh', flag: '🇺🇸' },
    { name: 'Dylan', flag: '🇺🇸' },
    { name: 'Dawn', flag: '🇺🇸' },
    { name: 'Summer', flag: '🇺🇸' },
    { name: 'Andy', flag: '🇺🇸' },
    { name: 'Zoe', flag: '🇺🇸' },
    { name: 'Alexis', flag: '🇺🇸' },
    { name: 'Michael', flag: '🇺🇸' },
    { name: 'Pete', flag: '🇺🇸' },
    { name: 'Brian', flag: '🇺🇸' },
    { name: 'Diana', flag: '🇺🇸' },
    { name: 'Kai', flag: '🇺🇸' },
    { name: 'Claire', flag: '🇺🇸' },
    { name: 'Nathan', flag: '🇺🇸' },
    { name: 'Audrey', flag: '🇺🇸' },
    { name: 'Melissa', flag: '🇬🇧' },
    { name: 'Will', flag: '🇬🇧' },
    { name: 'Gautam', flag: '🇮🇳' },
    { name: 'Luke', flag: '🇨🇳' },
    { name: 'Alexei', flag: '🇷🇺' },
    { name: 'Max', flag: '🇩🇪' },
    { name: 'Anna', flag: '🇩🇪' },
    { name: 'Antoine', flag: '🇫🇷' },
    { name: 'Jennie', flag: '🇰🇷' },
    { name: 'Kenji', flag: '🇯🇵' },
    { name: 'Lily', flag: '🇨🇳' },
    { name: 'Kevin', flag: '🇰🇷' },
    { name: 'Nova', flag: '🇮🇹' },
    { name: 'Marco', flag: '🇮🇹' },
    { name: 'Yuki', flag: '🇯🇵' },
    { name: 'Santiago', flag: '🇪🇸' },
    { name: 'Leo', flag: '🇨🇴' }
];

const VOICE_PILL_STYLE = 'bg-white text-stone-800 border-stone-200';
const AVAILABLE_VOICE_SAMPLE_NAMES = new Set([
    'Josh',
    'Dylan',
    'Dawn',
    'Summer',
    'Andy',
    'Zoe',
    'Alexis',
    'Michael',
    'Pete',
    'Brian',
    'Diana',
    'Kai',
    'Claire',
    'Nathan',
    'Audrey',
    'Melissa',
    'Will',
    'Gautam',
    'Luke',
    'Alexei',
    'Max',
    'Anna',
    'Antoine',
    'Jennie',
    'Kenji',
    'Lily',
    'Kevin',
    'Nova',
    'Marco',
    'Yuki',
    'Santiago',
    'Leo'
]);

const getVoiceLibrarySampleSrc = (voiceName) => (
    AVAILABLE_VOICE_SAMPLE_NAMES.has(voiceName)
        ? `/audio/voice-library/${encodeURIComponent(voiceName)}.wav`
        : null
);

export default function DemoLanding() {
    const [audioExampleIndex, setAudioExampleIndex] = useState(0);
    const [isPlayingExample, setIsPlayingExample] = useState(false);
    const [isStageDirectionsPlaying, setIsStageDirectionsPlaying] = useState(false);
    const [playingLibraryVoice, setPlayingLibraryVoice] = useState(null);

    const multilingualExamples = [
        { text: 'Yaar mujhe na aaj itni neend aa rahi thi meeting mein, like I was literally dying, but boss ke saamne kya karega, toh I just kept nodding like haan haan good point.', bg: '/textures/blue_texture.png', audio: '/audio/multilingual_1.wav' },
        { text: '오늘 진짜 너무 피곤해서 아무것도 하기 싫었는데. But then my friend called me and was like let\'s get chicken, and honestly that\'s the only thing that could\'ve saved my day.', bg: '/textures/green_texture.png', audio: '/audio/multilingual_2.wav' },
        { text: 'Ich war gestern im Supermarkt und die hatten literally nothing left, like the shelves were completely empty, und ich stand da so wie okay was soll ich jetzt eigentlich essen heute Abend.', bg: '/textures/red_texture.png', audio: '/audio/multilingual_3.wav' }
    ];

    const bilingualAudioRef = useRef(null);
    const bilingualRafRef = useRef(null);
    const [bilingualProgress, setBilingualProgress] = useState(0);

    const handleBilingualPlay = (index) => {
        const example = multilingualExamples[index];
        if (!example.audio) return;

        if (isPlayingExample) {
            bilingualAudioRef.current?.pause();
            cancelAnimationFrame(bilingualRafRef.current);
            setIsPlayingExample(false);
        } else {
            if (bilingualAudioRef.current) {
                bilingualAudioRef.current.pause();
                bilingualAudioRef.current.currentTime = 0;
            }
            cancelAnimationFrame(bilingualRafRef.current);
            setBilingualProgress(0);

            const audio = new Audio(example.audio);
            bilingualAudioRef.current = audio;

            const tick = () => {
                if (audio.duration > 0) {
                    setBilingualProgress(audio.currentTime / audio.duration);
                }
                bilingualRafRef.current = requestAnimationFrame(tick);
            };

            audio.onplay = () => {
                bilingualRafRef.current = requestAnimationFrame(tick);
            };
            audio.onended = () => {
                cancelAnimationFrame(bilingualRafRef.current);
                setIsPlayingExample(false);
                setBilingualProgress(0);
            };

            setIsPlayingExample(true);
            audio.play().catch(() => {});
        }
    };

    useEffect(() => {
        if (bilingualAudioRef.current) {
            bilingualAudioRef.current.pause();
            bilingualAudioRef.current.currentTime = 0;
        }
        cancelAnimationFrame(bilingualRafRef.current);
        setIsPlayingExample(false);
        setBilingualProgress(0);
    }, [audioExampleIndex]);

    const showcaseSamples = [
        {
            text: <>A <span className="underline">customer support</span> agent helps a caller find their missing package</>,
            audioQueue: Array.from({ length: 9 }, (_, i) => `/audio/customer_service_${i + 1}.wav`),
            bg: '/textures/blue_texture.png',
        },
        {
            text: <>A <span className="underline">self reflective</span> monologue on the nature of who we are</>,
            audioQueue: ['/audio/self_reflective_1.wav', '/audio/self_reflective_2.wav'],
            bg: '/textures/green_texture.png',
        },
        {
            text: <>A <span className="underline">therapist</span> speaks to a patient, who is stressed about their day</>,
            audioQueue: ['/audio/therapist_1.wav', '/audio/therapist_2.wav', '/audio/therapist_3.wav'],
            bg: '/textures/peach_texture.png',
        },
        {
            text: <>A <span className="underline">theatrical</span> retelling of a stressful day between friends</>,
            audioQueue: ['/audio/theatrical_1.wav', '/audio/theatrical_2.wav', '/audio/theatrical_3.wav', '/audio/theatrical_4.wav'],
            bg: '/textures/sand_texture.png',
        },
        {
            text: <>A <span className="underline">language learning</span> tutor helps a student pronounce words better</>,
            audioQueue: ['/audio/language_learning_1.wav', '/audio/language_learning_2.wav', '/audio/language_learning_3.wav'],
            bg: '/textures/lavender_texture.png',
        },
        {
            text: <>A <span className="underline">meditation coach</span> calms down a stressed patient</>,
            audioQueue: ['/audio/meditation_1.wav', '/audio/meditation_2.wav'],
            bg: '/textures/red_texture.png',
        },
    ];
    const [playingShowcaseIndex, setPlayingShowcaseIndex] = useState(null);
    const [showcaseProgress, setShowcaseProgress] = useState(0);
    const showcaseRafRef = useRef(null);
    const showcaseSourceRef = useRef(null);
    const showcaseCtxRef = useRef(null);
    const showcaseStartTimeRef = useRef(0);
    const showcaseTotalDurationRef = useRef(0);
    const showcasePlaybackTokenRef = useRef(0);
    const showcaseResetTimeoutRef = useRef(null);
    const stageDirectionsAudioRef = useRef(null);
    const stageDirectionsRafRef = useRef(null);
    const voiceLibraryAudioRef = useRef(null);
    const [stageDirectionsProgress, setStageDirectionsProgress] = useState(0);

    useEffect(() => {
        return () => {
            stopShowcase();
            if (showcaseCtxRef.current) {
                showcaseCtxRef.current.close().catch(() => {});
            }
            if (voiceLibraryAudioRef.current) {
                voiceLibraryAudioRef.current.pause();
                voiceLibraryAudioRef.current.currentTime = 0;
                voiceLibraryAudioRef.current = null;
            }
            if (stageDirectionsAudioRef.current) {
                stageDirectionsAudioRef.current.pause();
                stageDirectionsAudioRef.current.currentTime = 0;
                stageDirectionsAudioRef.current = null;
            }
            cancelAnimationFrame(stageDirectionsRafRef.current);
        };
    }, []);

    const stopVoiceLibrarySample = () => {
        if (voiceLibraryAudioRef.current) {
            voiceLibraryAudioRef.current.pause();
            voiceLibraryAudioRef.current.currentTime = 0;
            voiceLibraryAudioRef.current = null;
        }
        setPlayingLibraryVoice(null);
    };

    const handleVoiceLibrarySamplePlay = async (voiceName) => {
        const sampleSrc = getVoiceLibrarySampleSrc(voiceName);
        if (!sampleSrc) return;

        if (playingLibraryVoice === voiceName && voiceLibraryAudioRef.current && !voiceLibraryAudioRef.current.paused) {
            stopVoiceLibrarySample();
            return;
        }

        stopVoiceLibrarySample();

        const audio = new Audio(sampleSrc);
        voiceLibraryAudioRef.current = audio;
        setPlayingLibraryVoice(voiceName);

        audio.onended = () => {
            if (voiceLibraryAudioRef.current === audio) {
                voiceLibraryAudioRef.current = null;
            }
            setPlayingLibraryVoice(null);
        };

        try {
            await audio.play();
        } catch (error) {
            if (voiceLibraryAudioRef.current === audio) {
                voiceLibraryAudioRef.current = null;
            }
            setPlayingLibraryVoice(null);
            console.error('Voice library playback error:', error);
        }
    };

    const stopShowcase = () => {
        showcasePlaybackTokenRef.current += 1;
        if (showcaseResetTimeoutRef.current) {
            clearTimeout(showcaseResetTimeoutRef.current);
            showcaseResetTimeoutRef.current = null;
        }
        cancelAnimationFrame(showcaseRafRef.current);
        showcaseRafRef.current = null;
        if (showcaseSourceRef.current) {
            try {
                showcaseSourceRef.current.stop();
                showcaseSourceRef.current.disconnect();
            } catch (e) {
            }
            showcaseSourceRef.current = null;
        }
    };

    const handleShowcasePlay = async (index) => {
        if (playingShowcaseIndex === index) {
            stopShowcase();
            setPlayingShowcaseIndex(null);
            setShowcaseProgress(0);
            return;
        }

        stopShowcase();
        setShowcaseProgress(0);

        const queue = showcaseSamples[index].audioQueue;
        if (!queue || queue.length === 0) return;

        setPlayingShowcaseIndex(index);
        const playbackToken = showcasePlaybackTokenRef.current + 1;
        showcasePlaybackTokenRef.current = playbackToken;

        try {
            if (!showcaseCtxRef.current) {
                const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                showcaseCtxRef.current = new AudioContextClass();
            }
            const ctx = showcaseCtxRef.current;
            if (ctx.state === 'suspended') await ctx.resume();

            const buffers = await Promise.all(
                queue.map(async (src) => {
                    const res = await fetch(src);
                    const arrayBuf = await res.arrayBuffer();
                    return ctx.decodeAudioData(arrayBuf);
                })
            );
            if (showcasePlaybackTokenRef.current !== playbackToken) return;

            const totalLength = buffers.reduce((sum, b) => sum + b.length, 0);
            const sampleRate = buffers[0].sampleRate;
            const channels = buffers[0].numberOfChannels;
            const merged = ctx.createBuffer(channels, totalLength, sampleRate);

            let offset = 0;
            for (const buf of buffers) {
                for (let ch = 0; ch < channels; ch++) {
                    merged.getChannelData(ch).set(buf.getChannelData(ch), offset);
                }
                offset += buf.length;
            }

            const source = ctx.createBufferSource();
            source.buffer = merged;
            const gainNode = ctx.createGain();
            gainNode.gain.value = 1.778;
            source.connect(gainNode);
            gainNode.connect(ctx.destination);
            showcaseSourceRef.current = source;

            const totalDuration = merged.duration;
            showcaseTotalDurationRef.current = totalDuration;
            showcaseStartTimeRef.current = ctx.currentTime;

            source.start();

            const tick = () => {
                if (!showcaseSourceRef.current || showcasePlaybackTokenRef.current !== playbackToken) return;
                const elapsed = ctx.currentTime - showcaseStartTimeRef.current;
                const nextProgress = Math.min(elapsed / totalDuration, 1);
                setShowcaseProgress(prev => (nextProgress > prev ? nextProgress : prev));
                if (elapsed < totalDuration) {
                    showcaseRafRef.current = requestAnimationFrame(tick);
                }
            };
            showcaseRafRef.current = requestAnimationFrame(tick);

            source.onended = () => {
                if (showcasePlaybackTokenRef.current !== playbackToken) return;
                cancelAnimationFrame(showcaseRafRef.current);
                showcaseSourceRef.current = null;
                setShowcaseProgress(1);
                showcaseResetTimeoutRef.current = setTimeout(() => {
                    setPlayingShowcaseIndex(null);
                    setShowcaseProgress(0);
                    showcaseResetTimeoutRef.current = null;
                }, 150);
            };
        } catch (err) {
            console.error('Showcase playback error:', err);
            setPlayingShowcaseIndex(null);
            setShowcaseProgress(0);
        }
    };

    const handleStageDirectionsPlay = async () => {
        if (!stageDirectionsAudioRef.current) {
            const audio = new Audio('/audio/stage_directions_1.wav');
            audio.onended = () => {
                cancelAnimationFrame(stageDirectionsRafRef.current);
                setIsStageDirectionsPlaying(false);
                setStageDirectionsProgress(0);
            };
            stageDirectionsAudioRef.current = audio;
        }

        const audio = stageDirectionsAudioRef.current;
        if (isStageDirectionsPlaying) {
            audio.pause();
            cancelAnimationFrame(stageDirectionsRafRef.current);
            setIsStageDirectionsPlaying(false);
            return;
        }

        try {
            if (audio.ended) {
                audio.currentTime = 0;
                setStageDirectionsProgress(0);
            }

            const tick = () => {
                if (audio.duration > 0) {
                    setStageDirectionsProgress(audio.currentTime / audio.duration);
                }
                stageDirectionsRafRef.current = requestAnimationFrame(tick);
            };

            await audio.play();
            setIsStageDirectionsPlaying(true);
            stageDirectionsRafRef.current = requestAnimationFrame(tick);
        } catch (error) {
            setIsStageDirectionsPlaying(false);
            console.error('Stage directions playback error:', error);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAF9] font-['Inter'] text-stone-900 overflow-x-hidden selection:bg-stone-200">
            <main className="pt-8 md:pt-12 pb-20 px-4 md:px-6 w-full flex flex-col items-start bg-[#FAFAF9]">
                <div className="max-w-[1400px] mx-auto w-full px-0 flex flex-col items-start">
                    <div className="w-full mb-8 md:mb-16">
                        <p className="text-sm md:text-base uppercase tracking-[0.16em] text-stone-500 mb-4">
                            Welcome to Orpheus v1, the most capable TTS model ever.
                        </p>
                        <h1 className="text-2xl md:text-[1.75rem] font-normal font-['Inter'] tracking-tight leading-tight max-w-4xl text-stone-900">
                            Design beautiful interactive voice experiences,<br className="hidden md:block" />and <span className="text-stone-500 font-['Inter']">delight</span> your users.
                        </h1>
                    </div>
                    <div id="conversations" className="w-full mt-8 md:mt-12">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-8 h-8 rounded-full border border-stone-800 bg-transparent text-stone-900 flex items-center justify-center text-sm font-medium">1</span>
                            <span className="text-sm font-medium text-stone-900 uppercase tracking-wide">Create Breathtaking Conversations</span>
                        </div>
                        <p className="text-stone-900 text-2xl md:text-4xl font-light mb-10 max-w-3xl leading-snug">
                            Listen to conversations created by magical voices that give you goosebumps.
                        </p>

                        <div className="flex flex-col gap-4 md:gap-5">
                            <div className="flex flex-col gap-4 md:hidden">
                                {showcaseSamples.map((sample, index) => (
                                    <div
                                        key={index}
                                        className="w-full h-[280px] rounded-2xl p-6 flex flex-col justify-between relative overflow-hidden"
                                        style={sample.bg.startsWith('/') ? { backgroundImage: `url(${sample.bg})`, backgroundSize: 'cover', backgroundPosition: 'center' } : { backgroundColor: sample.bg }}
                                    >
                                        <p className="text-stone-900 text-[22px] font-light leading-relaxed">
                                            {sample.text}
                                        </p>
                                        <div className="flex justify-end">
                                            <button
                                                onClick={() => handleShowcasePlay(index)}
                                                className="relative w-14 h-14 rounded-full flex items-center justify-center cursor-pointer active:scale-95 transition-transform group"
                                            >
                                                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 56 56">
                                                    <circle cx="28" cy="28" r="25" fill="#1c1917" stroke={playingShowcaseIndex === index ? 'rgba(255,255,255,0.2)' : 'transparent'} strokeWidth="3" className="group-hover:fill-stone-700 transition-colors" />
                                                    {playingShowcaseIndex === index && (
                                                        <circle cx="28" cy="28" r="25" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 25}`} strokeDashoffset={`${2 * Math.PI * 25 * (1 - showcaseProgress)}`} />
                                                    )}
                                                </svg>
                                                <span className="relative z-10 text-white">
                                                    {playingShowcaseIndex === index ? (
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
                                                    ) : (
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21" /></svg>
                                                    )}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {[showcaseSamples.slice(0, 3), showcaseSamples.slice(3, 6)].map((row, rowIndex) => (
                                <div key={rowIndex} className="hidden md:flex gap-5">
                                    {row.map((sample, colIndex) => {
                                        const index = rowIndex * 3 + colIndex;
                                        return (
                                            <div
                                                key={index}
                                                className="flex-shrink-0 w-[380px] h-[340px] rounded-2xl p-8 flex flex-col justify-between relative overflow-hidden"
                                                style={sample.bg.startsWith('/') ? { backgroundImage: `url(${sample.bg})`, backgroundSize: 'cover', backgroundPosition: 'center' } : { backgroundColor: sample.bg }}
                                            >
                                                <p className="text-stone-900 text-xl md:text-2xl font-light leading-relaxed">
                                                    {sample.text}
                                                </p>

                                                <div className="flex justify-end">
                                                    <button
                                                        onClick={() => handleShowcasePlay(index)}
                                                        className="relative w-14 h-14 rounded-full flex items-center justify-center cursor-pointer active:scale-95 transition-transform group"
                                                    >
                                                        <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 56 56">
                                                            <circle
                                                                cx="28"
                                                                cy="28"
                                                                r="25"
                                                                fill="#1c1917"
                                                                stroke={playingShowcaseIndex === index ? 'rgba(255,255,255,0.2)' : 'transparent'}
                                                                strokeWidth="3"
                                                                className="group-hover:fill-stone-700 transition-colors"
                                                            />
                                                            {playingShowcaseIndex === index && (
                                                                <circle
                                                                    cx="28"
                                                                    cy="28"
                                                                    r="25"
                                                                    fill="none"
                                                                    stroke="white"
                                                                    strokeWidth="3"
                                                                    strokeLinecap="round"
                                                                    strokeDasharray={`${2 * Math.PI * 25}`}
                                                                    strokeDashoffset={`${2 * Math.PI * 25 * (1 - showcaseProgress)}`}
                                                                />
                                                            )}
                                                        </svg>
                                                        <span className="relative z-10 text-white">
                                                            {playingShowcaseIndex === index ? (
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                                    <rect x="6" y="4" width="4" height="16" rx="1" />
                                                                    <rect x="14" y="4" width="4" height="16" rx="1" />
                                                                </svg>
                                                            ) : (
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                                    <polygon points="6 3 20 12 6 21" />
                                                                </svg>
                                                            )}
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div id="features" className="w-full mt-32 md:mt-52">
                        <div id="stage-directions" className="mb-32 md:mb-52">
                            <div className="flex items-center gap-3 mb-10">
                                <span className="w-8 h-8 rounded-full border border-stone-800 bg-transparent text-stone-900 flex items-center justify-center text-sm font-medium">2</span>
                                <span className="text-sm font-medium text-stone-900 uppercase tracking-wide">Stage Directions</span>
                            </div>
                            <div className="max-w-6xl border-l-2 border-stone-900 pl-8">
                                <p className="text-stone-900 text-2xl md:text-4xl leading-relaxed md:leading-relaxed font-light italic">
                                    <span className="text-emerald-800 not-italic bg-stone-100 border border-stone-300 px-2 py-1 rounded">[slurring slightly]</span> No but listen, listen-you are genuinely <span className="text-emerald-800 not-italic bg-stone-100 border border-stone-300 px-2 py-1 rounded">[hiccup]</span> one of the best people I know and I don't say that enough and I'm <span className="text-emerald-800 not-italic bg-stone-100 border border-stone-300 px-2 py-1 rounded">[getting emotional]</span> I'm really glad you're my friend.
                                </p>
                                <div className="flex justify-end mt-6">
                                    <button
                                        onClick={handleStageDirectionsPlay}
                                        className="relative w-14 h-14 rounded-full flex items-center justify-center cursor-pointer active:scale-95 transition-transform group"
                                    >
                                        <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 56 56">
                                            <circle
                                                cx="28"
                                                cy="28"
                                                r="25"
                                                fill="#1c1917"
                                                stroke={isStageDirectionsPlaying ? 'rgba(255,255,255,0.2)' : 'transparent'}
                                                strokeWidth="3"
                                                className="group-hover:fill-stone-700 transition-colors"
                                            />
                                            {isStageDirectionsPlaying && (
                                                <circle
                                                    cx="28"
                                                    cy="28"
                                                    r="25"
                                                    fill="none"
                                                    stroke="white"
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                    strokeDasharray={`${2 * Math.PI * 25}`}
                                                    strokeDashoffset={`${2 * Math.PI * 25 * (1 - stageDirectionsProgress)}`}
                                                />
                                            )}
                                        </svg>
                                        <span className="relative z-10 text-white">
                                            {isStageDirectionsPlaying ? (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                    <rect x="6" y="4" width="4" height="16" rx="1" />
                                                    <rect x="14" y="4" width="4" height="16" rx="1" />
                                                </svg>
                                            ) : (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                    <polygon points="6 3 20 12 6 21" />
                                                </svg>
                                            )}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div id="multilingual" className="w-full bg-[#F2F1ED] border border-stone-200 rounded-lg p-8 md:p-16 mb-24 md:mb-40">
                            <div className="relative" style={{ overflow: 'visible' }}>
                                <div className="absolute top-0 -left-6 -right-6 h-px bg-stone-900" />
                                <div className="absolute bottom-0 -left-6 -right-6 h-px bg-stone-900" />
                                <div className="absolute left-0 -top-6 -bottom-6 w-px bg-stone-900" />
                                <div className="absolute right-0 -top-6 -bottom-6 w-px bg-stone-900" />

                                <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-stretch p-8 md:p-12">
                                    <div className="flex-1 flex flex-col justify-center">
                                        <div className="flex items-center gap-3 mb-6">
                                            <span className="w-8 h-8 rounded-full border border-stone-800 bg-transparent text-stone-900 flex items-center justify-center text-sm font-medium">3</span>
                                            <span className="text-sm font-medium text-stone-900 uppercase tracking-wide">Natively Multilingual</span>
                                        </div>
                                        <p className="text-stone-900 text-[17px] md:text-[23px] leading-loose font-light">
                                            Orpheus effortlessly switches in and out of languages multiple times in a sentence as fluently as a bilingual speaker across{' '}
                                            <span className="relative inline-flex group align-baseline underline">
                                                11 languages
                                                <span className="pointer-events-none absolute left-1/2 top-full z-10 mt-3 w-[320px] max-w-[80vw] -translate-x-1/2 rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm leading-relaxed text-stone-600 opacity-0 shadow-[0_16px_40px_rgba(28,25,23,0.12)] transition-all duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
                                                    English, Hindi, Mandarin, German, Japanese, French, Italian, Portuguese, Russian, Spanish, Korean
                                                </span>
                                            </span>.
                                        </p>
                                    </div>
                                    <div className="flex-1 flex flex-col items-center justify-center min-h-[300px] md:min-h-[550px] rounded-lg relative px-8 md:px-12 transition-all duration-500" style={{ backgroundImage: `url(${multilingualExamples[audioExampleIndex].bg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                                        <div className="flex-1 flex items-center justify-center w-full">
                                            <p key={audioExampleIndex} className="text-stone-900 text-xl md:text-3xl font-light text-center leading-relaxed" style={{ animation: 'featureSlideUp 0.4s ease-out forwards' }}>
                                                {multilingualExamples[audioExampleIndex].text}
                                            </p>
                                        </div>

                                        <div className="w-full flex items-center justify-between mb-6">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setAudioExampleIndex(prev => prev === 0 ? multilingualExamples.length - 1 : prev - 1)}
                                                    className="w-9 h-9 rounded-full bg-stone-900 flex items-center justify-center text-white hover:bg-stone-700 transition-colors"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                                                </button>
                                                <button
                                                    onClick={() => setAudioExampleIndex(prev => prev === multilingualExamples.length - 1 ? 0 : prev + 1)}
                                                    className="w-9 h-9 rounded-full bg-stone-900 flex items-center justify-center text-white hover:bg-stone-700 transition-colors"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                                                </button>
                                            </div>

                                            <button
                                                onClick={() => handleBilingualPlay(audioExampleIndex)}
                                                disabled={!multilingualExamples[audioExampleIndex].audio}
                                                className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-all cursor-pointer active:scale-95 group ${!multilingualExamples[audioExampleIndex].audio ? 'opacity-30 cursor-not-allowed' : ''}`}
                                            >
                                                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 56 56">
                                                    <circle
                                                        cx="28"
                                                        cy="28"
                                                        r="25"
                                                        fill="#1c1917"
                                                        stroke={isPlayingExample ? 'rgba(255,255,255,0.2)' : 'transparent'}
                                                        strokeWidth="3"
                                                        className="group-hover:fill-stone-700 transition-colors"
                                                    />
                                                    {isPlayingExample && (
                                                        <circle
                                                            cx="28"
                                                            cy="28"
                                                            r="25"
                                                            fill="none"
                                                            stroke="white"
                                                            strokeWidth="3"
                                                            strokeLinecap="round"
                                                            strokeDasharray={`${2 * Math.PI * 25}`}
                                                            strokeDashoffset={`${2 * Math.PI * 25 * (1 - bilingualProgress)}`}
                                                            style={{ transition: 'stroke-dashoffset 0.1s linear' }}
                                                        />
                                                    )}
                                                </svg>
                                                <span className="relative z-10 text-white">
                                                    {isPlayingExample ? (
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
                                                    ) : (
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21" /></svg>
                                                    )}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div id="production" className="w-full bg-[#1c1b16] rounded-lg p-10 md:p-20 overflow-hidden">
                            <div className="flex items-center gap-3 mb-16">
                                <span className="w-8 h-8 rounded-full border border-stone-500 bg-transparent text-white flex items-center justify-center text-sm font-medium">4</span>
                                <span className="text-sm font-medium text-stone-400 uppercase tracking-wide">Built for Production</span>
                            </div>

                            <div className="flex flex-col md:flex-row gap-10 md:gap-20 items-center mb-0 pb-16 border-b border-white/[0.08]">
                                <div className="flex-1">
                                    <p className="text-xs uppercase tracking-widest text-stone-500 mb-4">Latency</p>
                                    <h3 className="text-3xl md:text-5xl font-light text-white mb-6 tracking-tight">Ultra-Low Latency</h3>
                                    <p className="text-stone-400 text-lg leading-relaxed max-w-lg mb-8">
                                        Industry-leading time-to-first-byte across both model tiers. Our p99 latency stays within 30ms of root latency.
                                    </p>

                                    <table className="w-full max-w-md border-collapse">
                                        <thead>
                                            <tr className="border-b border-white/[0.12]">
                                                <th className="text-left text-xs uppercase tracking-widest text-stone-500 font-medium py-3 pr-4">Model</th>
                                                <th className="text-right text-xs uppercase tracking-widest text-stone-500 font-medium py-3 pl-4">TTFB</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="border-b border-white/[0.06]">
                                                <td className="py-4 pr-4">
                                                    <span className="text-white text-lg font-light">Orpheus v1 Flash</span>
                                                </td>
                                                <td className="py-4 pl-4 text-right">
                                                    <span className="text-white text-2xl md:text-3xl font-light tabular-nums">120<span className="text-stone-500 text-base ml-0.5">ms</span></span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="py-4 pr-4">
                                                    <span className="text-white text-lg font-light">Orpheus v1 Base</span>
                                                </td>
                                                <td className="py-4 pl-4 text-right">
                                                    <span className="text-white text-2xl md:text-3xl font-light tabular-nums">190<span className="text-stone-500 text-base ml-0.5">ms</span></span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="flex-shrink-0 w-full md:w-auto">
                                    <svg width="280" height="160" viewBox="0 0 280 160" fill="none">
                                        <line x1="40" y1="20" x2="40" y2="130" stroke="white" strokeOpacity="0.06" strokeWidth="1" />
                                        <line x1="120" y1="20" x2="120" y2="130" stroke="white" strokeOpacity="0.06" strokeWidth="1" />
                                        <line x1="200" y1="20" x2="200" y2="130" stroke="white" strokeOpacity="0.06" strokeWidth="1" />
                                        <line x1="20" y1="40" x2="260" y2="40" stroke="white" strokeOpacity="0.06" strokeWidth="1" />
                                        <line x1="20" y1="80" x2="260" y2="80" stroke="white" strokeOpacity="0.06" strokeWidth="1" />
                                        <line x1="20" y1="120" x2="260" y2="120" stroke="white" strokeOpacity="0.06" strokeWidth="1" />
                                        <polyline points="30,100 60,95 90,85 120,70 150,55 180,40 210,35 240,30" stroke="white" strokeOpacity="0.12" strokeWidth="1.5" fill="none" strokeDasharray="4 4" />
                                        <polyline points="30,110 60,108 90,100 120,88 150,70 180,50 210,38 240,28" stroke="white" strokeOpacity="0.4" strokeWidth="2" fill="none" />
                                        <circle cx="240" cy="28" r="4" fill="none" stroke="white" strokeOpacity="0.5" strokeWidth="1.5" />
                                        <circle cx="240" cy="28" r="1.5" fill="white" fillOpacity="0.8" />
                                        <text x="240" y="60" fill="white" fillOpacity="0.15" fontSize="16" textAnchor="end" fontWeight="300">120ms</text>
                                        <text x="20" y="145" fill="white" fillOpacity="0.15" fontSize="9">0</text>
                                        <text x="240" y="145" fill="white" fillOpacity="0.15" fontSize="9">time</text>
                                    </svg>
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row pt-16">
                                <div className="flex-1 pr-0 md:pr-16 pb-12 md:pb-0">
                                    <p className="text-xs uppercase tracking-widest text-stone-500 mb-4">Professional and One-Shot Voice Cloning</p>
                                    <h3 className="text-2xl md:text-3xl font-light text-white mb-5 tracking-tight">Custom Voices</h3>
                                    <p className="text-stone-400 text-lg leading-relaxed mb-8">
                                        Create unique, brand-specific voices tailored to your business. Professional voice cloning with just a few hours of audio data, delivering voices indistinguishable from the original.
                                    </p>
                                    <svg width="100%" height="48" viewBox="0 0 300 48" fill="none" preserveAspectRatio="xMinYMid meet">
                                        {[...Array(50)].map((_, i) => {
                                            const heights = [4, 8, 14, 22, 18, 28, 36, 24, 16, 32, 40, 30, 20, 34, 26, 12, 22, 36, 28, 18, 32, 42, 24, 14, 30, 20, 10, 26, 36, 32, 16, 22, 8, 18, 6, 12, 20, 28, 16, 34, 24, 10, 18, 30, 22, 8, 14, 26, 12, 6];
                                            const h = heights[i] || 10;
                                            return (
                                                <line key={i} x1={i * 6} y1={24 - h / 2} x2={i * 6} y2={24 + h / 2} stroke="white" strokeOpacity={0.15 + (h / 42) * 0.2} strokeWidth="2" strokeLinecap="round" />
                                            );
                                        })}
                                    </svg>
                                </div>

                                <div className="hidden md:block w-px bg-white/[0.08] self-stretch" />

                                <div className="flex-1 pl-0 md:pl-16 pt-12 md:pt-0 border-t md:border-t-0 border-white/[0.08]">
                                    <p className="text-xs uppercase tracking-widest text-stone-500 mb-4">Infrastructure</p>
                                    <h3 className="text-2xl md:text-3xl font-light text-white mb-5 tracking-tight">Enterprise Deployments</h3>
                                    <p className="text-stone-400 text-lg leading-relaxed mb-8">
                                        Run Orpheus on your own infrastructure with full control. On-premise or private cloud deployments with priority support and SLA guarantees.
                                    </p>
                                    <svg width="200" height="80" viewBox="0 0 200 80" fill="none">
                                        <line x1="100" y1="12" x2="30" y2="45" stroke="white" strokeOpacity="0.15" strokeWidth="1" />
                                        <line x1="100" y1="12" x2="100" y2="45" stroke="white" strokeOpacity="0.15" strokeWidth="1" />
                                        <line x1="100" y1="12" x2="170" y2="45" stroke="white" strokeOpacity="0.15" strokeWidth="1" />
                                        <circle cx="100" cy="12" r="8" fill="none" stroke="white" strokeOpacity="0.25" strokeWidth="1" />
                                        <circle cx="100" cy="12" r="3" fill="none" stroke="white" strokeOpacity="0.4" strokeWidth="1" />
                                        <rect x="16" y="40" width="28" height="20" rx="3" fill="none" stroke="white" strokeOpacity="0.2" strokeWidth="1" />
                                        <line x1="22" y1="47" x2="38" y2="47" stroke="white" strokeOpacity="0.1" strokeWidth="1" />
                                        <line x1="22" y1="52" x2="32" y2="52" stroke="white" strokeOpacity="0.08" strokeWidth="1" />
                                        <rect x="86" y="40" width="28" height="20" rx="3" fill="none" stroke="white" strokeOpacity="0.2" strokeWidth="1" />
                                        <line x1="92" y1="47" x2="108" y2="47" stroke="white" strokeOpacity="0.1" strokeWidth="1" />
                                        <line x1="92" y1="52" x2="102" y2="52" stroke="white" strokeOpacity="0.08" strokeWidth="1" />
                                        <rect x="156" y="40" width="28" height="20" rx="3" fill="none" stroke="white" strokeOpacity="0.2" strokeWidth="1" />
                                        <line x1="162" y1="47" x2="178" y2="47" stroke="white" strokeOpacity="0.1" strokeWidth="1" />
                                        <line x1="162" y1="52" x2="172" y2="52" stroke="white" strokeOpacity="0.08" strokeWidth="1" />
                                        <line x1="30" y1="60" x2="30" y2="72" stroke="white" strokeOpacity="0.08" strokeWidth="1" strokeDasharray="3 3" />
                                        <line x1="100" y1="60" x2="100" y2="72" stroke="white" strokeOpacity="0.08" strokeWidth="1" strokeDasharray="3 3" />
                                        <line x1="170" y1="60" x2="170" y2="72" stroke="white" strokeOpacity="0.08" strokeWidth="1" strokeDasharray="3 3" />
                                        <line x1="20" y1="72" x2="180" y2="72" stroke="white" strokeOpacity="0.1" strokeWidth="1" />
                                    </svg>
                                </div>
                            </div>

                            <div className="mt-16 pt-16 border-t border-white/[0.08]">
                                <a href="mailto:elias@canopylabs.ai" className="inline-flex items-center gap-3 text-white text-lg font-medium hover:opacity-70 transition-opacity">
                                    Contact Sales
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12h14M12 5l7 7-7 7" />
                                    </svg>
                                </a>
                            </div>
                        </div>

                        <div id="voices" className="w-full mt-24 md:mt-32">
                            <div className="rounded-[2rem] border border-stone-200 bg-[linear-gradient(135deg,#f5f5f4_0%,#fafaf9_45%,#f0fdf4_100%)] p-5 md:p-8">
                                <div className="mb-8 md:mb-10">
                                    <div className="flex items-center gap-3 mb-4">
                                        <span className="w-8 h-8 rounded-full border border-stone-800 bg-transparent text-stone-900 flex items-center justify-center text-sm font-medium">5</span>
                                        <span className="text-sm font-medium text-stone-900 uppercase tracking-wide">Voice Library</span>
                                    </div>

                                    <p className="text-stone-900 text-2xl md:text-4xl font-light max-w-3xl leading-snug">
                                        Begin building with our library of 30+ voices.
                                    </p>
                                </div>

                                <div className="rounded-[1.5rem] border border-white/80 bg-white/80 backdrop-blur-sm p-4 md:p-5 shadow-[0_12px_40px_rgba(28,25,23,0.06)]">
                                    <div className="flex flex-wrap gap-2">
                                        {VOICE_CATALOG.map((voice) => {
                                            const sampleSrc = getVoiceLibrarySampleSrc(voice.name);

                                            return (
                                            <button
                                                key={voice.name}
                                                type="button"
                                                onClick={() => sampleSrc && handleVoiceLibrarySamplePlay(voice.name)}
                                                className={`inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm md:text-[15px] font-medium leading-none tracking-tight ${VOICE_PILL_STYLE} ${sampleSrc ? 'cursor-pointer' : 'cursor-default'}`}
                                            >
                                                <span>{voice.flag}</span>
                                                <span>{voice.name}</span>
                                            </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <footer id="footer" className="w-full px-4 md:px-6 mt-16 md:mt-24 pb-12">
                <div className="max-w-[1400px] mx-auto">
                    <div className="bg-stone-100 rounded-lg p-10 md:p-16">
                        <div className="flex flex-col md:flex-row justify-between items-start gap-12">
                            <div className="flex flex-col gap-6">
                                <div className="flex items-center gap-3">
                                    <img src={webLogo} alt="Canopy Labs" className="h-8" />
                                </div>
                                <a href="mailto:elias@canopylabs.ai" className="text-stone-500 hover:text-stone-900 transition-colors">
                                    elias@canopylabs.ai
                                </a>
                            </div>

                            <div className="flex flex-wrap gap-12 md:gap-20">
                                <div className="flex flex-col gap-4">
                                    <h4 className="text-stone-900 font-medium mb-2">Product</h4>
                                    <span className="text-stone-500 hover:text-stone-900 transition-colors cursor-pointer">Orpheus v1</span>
                                </div>
                                <div className="flex flex-col gap-4">
                                    <h4 className="text-stone-900 font-medium mb-2">Company</h4>
                                    <a href="https://canopylabs.ai" className="text-stone-500 hover:text-stone-900 transition-colors">Canopy Labs</a>
                                </div>
                                <div className="flex flex-col gap-4">
                                    <h4 className="text-stone-900 font-medium mb-2">Legal</h4>
                                    <a href="https://drive.google.com/file/d/1BbKhSZBD1cmzwRpO-fsMCDfc5Tk8rr1H/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="text-stone-500 hover:text-stone-900 transition-colors">Terms of Service</a>
                                </div>
                            </div>
                        </div>

                        <div className="mt-16 pt-8 border-t border-stone-200">
                            <p className="text-stone-400 text-sm">
                                © {new Date().getFullYear()} Canopy Labs. All rights reserved.
                            </p>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
